<script setup>
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';

const MAX_CHAT_HEADS = 5;

const router = useRouter();
const { t } = useI18n();
const { accountId } = useAccount();
const { uiSettings, updateUISettings } = useUISettings();
const currentChat = useMapGetter('getSelectedChat');

const storedHeads = computed(() => uiSettings.value?.chat_heads ?? []);

// ui_settings lives on the user while conversation ids are scoped to an account,
// so heads from another account must never be rendered or navigated to.
const headsInAccount = computed(() =>
  storedHeads.value.filter(head => head.accountId === accountId.value)
);

// The conversation already on screen does not need a shortcut to itself.
const visibleHeads = computed(() =>
  headsInAccount.value.filter(head => head.id !== currentChat.value?.id)
);

const identity = heads =>
  heads.map(head => `${head.accountId}:${head.id}`).join();

const persist = heads => updateUISettings({ chat_heads: heads });

const removeStoredHead = id => {
  const next = storedHeads.value.filter(
    item => !(item.id === id && item.accountId === accountId.value)
  );
  // Every ui_settings write replaces the whole current user object in the store,
  // so skip the request when the dock would not actually change.
  if (identity(next) === identity(storedHeads.value)) return;
  persist(next);
};

// True once the agent has actually replied to the open conversation, i.e. the most
// recent customer-facing message is an outgoing message the agent sent (not the
// automated acknowledgement, whose sender is not a User, and not a private note).
const currentChatAnsweredByAgent = computed(() => {
  const messages = currentChat.value?.messages ?? [];
  const lastReplyable = [...messages]
    .reverse()
    .find(message => message.message_type !== MESSAGE_TYPE.ACTIVITY);

  return (
    !!lastReplyable &&
    lastReplyable.message_type === MESSAGE_TYPE.OUTGOING &&
    lastReplyable.sender_type === 'User' &&
    !lastReplyable.private
  );
});

watch(
  () => currentChat.value?.id,
  id => {
    // Route and filter changes clear the selected chat, which fires this watcher
    // with an empty id. Writing then would push a junk entry into the dock.
    if (!id) return;

    // Don't add a head for a conversation the agent has already answered.
    if (currentChatAnsweredByAgent.value) return;

    const { name = '', thumbnail = '' } = currentChat.value?.meta?.sender ?? {};
    const head = { id, accountId: accountId.value, name, thumbnail };

    const others = storedHeads.value.filter(
      item => item.accountId !== accountId.value
    );
    const rest = headsInAccount.value.filter(item => item.id !== id);
    const next = [...[head, ...rest].slice(0, MAX_CHAT_HEADS), ...others];

    if (identity(next) === identity(storedHeads.value)) return;
    persist(next);
  },
  { immediate: true }
);

// When the agent replies to the open conversation, drop it from the dock so answered
// conversations stop piling up as chat heads.
watch(currentChatAnsweredByAgent, answered => {
  const id = currentChat.value?.id;
  if (answered && id) removeStoredHead(id);
});

const openConversation = head => {
  router.push(
    frontendURL(conversationUrl({ accountId: accountId.value, id: head.id }))
  );
};

const removeHead = head => {
  persist(
    storedHeads.value.filter(
      item => !(item.id === head.id && item.accountId === head.accountId)
    )
  );
};
</script>

<template>
  <div
    v-if="visibleHeads.length"
    class="fixed z-40 flex flex-col gap-2 -translate-y-1/2 top-1/2 ltr:right-3 rtl:left-3"
  >
    <div
      v-for="head in visibleHeads"
      :key="`${head.accountId}-${head.id}`"
      class="relative group"
    >
      <button
        type="button"
        :title="head.name || t('CHAT_HEADS.UNKNOWN_CONTACT')"
        class="block transition-all rounded-full shadow-md ring-2 ring-n-weak hover:ring-n-brand"
        @click="openConversation(head)"
      >
        <Avatar
          :name="head.name || t('CHAT_HEADS.UNKNOWN_CONTACT')"
          :src="head.thumbnail"
          :size="40"
          rounded-full
        />
      </button>
      <button
        type="button"
        :aria-label="t('CHAT_HEADS.REMOVE')"
        class="absolute items-center justify-center hidden w-4 h-4 rounded-full -top-1 ltr:-left-1 rtl:-right-1 bg-n-solid-3 text-n-slate-12 ring-1 ring-n-weak group-hover:flex"
        @click.stop="removeHead(head)"
      >
        <span class="i-lucide-x size-3" />
      </button>
    </div>
  </div>
  <template v-else />
</template>
